// Diagnostic interceptor for the "URL update succeeds locally but reverts on server" bug.
//
// Goal: capture exactly what `url` and `testurl` values the frontend is sending
// to /api/v1/surveys/update/<id> at the moment of save — BEFORE encryption — so
// we can compare against what the backend receives and what the DB writes.
//
// How to enable on production (no rebuild needed if the file is already deployed):
//   1. Open DevTools console at panel.dataxing.com
//   2. Run: localStorage.setItem('diagnoseUrlUpdate', '1')
//   3. Hard reload the page (Ctrl-Shift-R)
//   4. Edit a survey URL and click Save
//   5. In DevTools console, the diagnostic blocks will print live
//   6. Right-click console output → "Save as..." (Chrome) to export, or copy
//   7. To turn off: localStorage.removeItem('diagnoseUrlUpdate'); reload
//
// What it logs:
//   - The endpoint URL of the outgoing request
//   - The unencrypted request body (focused on url, testurl, childrenData)
//   - The HTTP status of the response
//   - The decrypted response body (status, message)
//   - A unique correlation id you can match against the backend pm2 log

import { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

const FLAG_KEY = 'diagnoseUrlUpdate';
const TARGET_PATH_FRAGMENT = '/surveys/update/';

function isEnabled(): boolean {
    try {
        return typeof window !== 'undefined' && window.localStorage.getItem(FLAG_KEY) === '1';
    } catch {
        return false;
    }
}

function shortId(): string {
    return Math.random().toString(36).slice(2, 8);
}

function summarize(data: any): Record<string, any> {
    if (!data || typeof data !== 'object') return { _raw: data };
    const out: Record<string, any> = {
        url: data.url,
        url_length: typeof data.url === 'string' ? data.url.length : null,
        testurl: data.testurl,
        testurl_length: typeof data.testurl === 'string' ? data.testurl.length : null,
        surveyType: data.surveyType,
        userAuthId_present: !!data.userAuthId,
        encryptiondta: data.encryptiondta,
        childrenData_count: Array.isArray(data.childrenData) ? data.childrenData.length : 0,
    };
    if (Array.isArray(data.childrenData)) {
        out.childrenData_summary = data.childrenData.map((c: any) => ({
            id: c?.id,
            shortName: c?.country?.shortName ?? c?.shortName,
            url: c?.url,
            testurl: c?.testurl,
            overriddenFields: c?.overriddenFields,
        }));
    }
    return out;
}

/**
 * Attach a request/response interceptor pair to the given axios instance.
 * Logs only when localStorage.diagnoseUrlUpdate === '1' AND the request URL
 * contains "/surveys/update/". Inert otherwise — safe to leave in production.
 *
 * Important: this MUST be installed BEFORE the existing encryption interceptor
 * so that the request handler sees the plain (un-encrypted) data. axios runs
 * request interceptors in REVERSE order of registration, so install this
 * AFTER the encryption interceptor in the source file.
 */
export function installUrlUpdateDiagnostics(api: AxiosInstance): void {
    api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        if (!isEnabled()) return config;
        const url = config.url || '';
        if (!url.includes(TARGET_PATH_FRAGMENT)) return config;

        const corr = shortId();
        (config as any).__diagnoseCorr = corr;
        (config as any).__diagnoseStart = Date.now();

        // At this point `config.data` is the PLAIN object (encryption
        // interceptor runs AFTER this one). Snapshot the relevant fields.
        // eslint-disable-next-line no-console
        console.group(`[diagnose-url-update ${corr}] OUTGOING ${config.method?.toUpperCase()} ${url}`);
        // eslint-disable-next-line no-console
        console.log('payload (pre-encryption):', summarize(config.data));
        try {
            // eslint-disable-next-line no-console
            console.log('payload (full, JSON):', JSON.parse(JSON.stringify(config.data)));
        } catch {
            // eslint-disable-next-line no-console
            console.log('payload (full): <unserialisable>');
        }
        // eslint-disable-next-line no-console
        console.groupEnd();
        return config;
    });

    api.interceptors.response.use(
        (res: AxiosResponse) => {
            if (!isEnabled()) return res;
            const url = res.config?.url || '';
            if (!url.includes(TARGET_PATH_FRAGMENT)) return res;
            const corr = (res.config as any).__diagnoseCorr || shortId();
            const took = (res.config as any).__diagnoseStart
                ? Date.now() - (res.config as any).__diagnoseStart
                : null;
            // eslint-disable-next-line no-console
            console.group(`[diagnose-url-update ${corr}] RESPONSE status=${res.status} took=${took}ms ${url}`);
            // eslint-disable-next-line no-console
            console.log('decrypted body:', res.data);
            // eslint-disable-next-line no-console
            console.groupEnd();
            return res;
        },
        (error: any) => {
            if (!isEnabled()) return Promise.reject(error);
            const url = error?.config?.url || '';
            if (!url.includes(TARGET_PATH_FRAGMENT)) return Promise.reject(error);
            const corr = (error.config as any)?.__diagnoseCorr || shortId();
            const took = (error.config as any)?.__diagnoseStart
                ? Date.now() - (error.config as any).__diagnoseStart
                : null;
            // eslint-disable-next-line no-console
            console.group(`[diagnose-url-update ${corr}] ERROR took=${took}ms ${url}`);
            // eslint-disable-next-line no-console
            console.log('status:', error?.response?.status);
            // eslint-disable-next-line no-console
            console.log('message:', error?.message);
            // eslint-disable-next-line no-console
            console.log('response.data:', error?.response?.data);
            // eslint-disable-next-line no-console
            console.groupEnd();
            return Promise.reject(error);
        },
    );

    // One-time helpful banner when the file is loaded with the flag on
    if (isEnabled()) {
        // eslint-disable-next-line no-console
        console.log(
            '%c[diagnose-url-update] enabled — survey update calls will be logged. ' +
            'Disable with: localStorage.removeItem(\'diagnoseUrlUpdate\') and reload.',
            'background:#ffeaa7;color:#2d3436;padding:2px 6px;border-radius:3px;',
        );
    }
}

/**
 * Optional helper to log form state at submit time. Call this from the
 * Form.tsx submit handler right before invoking SurveysAPI.update.
 */
export function logSubmitState(label: string, state: any): void {
    if (!isEnabled()) return;
    // eslint-disable-next-line no-console
    console.group(`[diagnose-url-update] FORM SUBMIT ${label}`);
    // eslint-disable-next-line no-console
    console.log('state.url:', state?.url, '(len=', state?.url?.length, ')');
    // eslint-disable-next-line no-console
    console.log('state.testurl:', state?.testurl, '(len=', state?.testurl?.length, ')');
    // eslint-disable-next-line no-console
    console.log('state.childStates keys:', state?.childStates ? Object.keys(state.childStates) : null);
    if (state?.childStates) {
        for (const [sn, cs] of Object.entries(state.childStates)) {
            const c = cs as any;
            // eslint-disable-next-line no-console
            console.log(`  child[${sn}]: url=${c?.url} testurl=${c?.testurl} overriddenFields=${JSON.stringify(c?.overriddenFields)}`);
        }
    }
    // eslint-disable-next-line no-console
    console.groupEnd();
}
